/** tm/pages/ConsultationPage.tsx — 동행상담 v5 (캘린더 + 하단 목록만) */
import { useState, useMemo, useEffect, useRef } from 'react';
import type { TMStore } from '../hooks';
import type { Consultation, CandidateSlot, FP } from '../types';
import { typingPhone } from '../../leads/calc';   // 전화번호 하이픈 자동(2026-10-19)
import { today } from '../hooks';
import { C, T, R, Badge, Btn, Field, SearchInput } from '../components/ui';
import ConsultConfirmModal from '../components/ConsultConfirmModal';
import ConsultPeekFlow from '../components/ConsultPeekFlow';
import WmMultiPicker from '../components/WmMultiPicker';
import * as api from '../api';
import type { InsuranceCompany } from '../api';
import * as lecApi from '../api-lectures';
import type { Lecture } from '../api-lectures';
import { consultBusy, lectureBusy, findConflicts, clashIds, CONSULT_MIN } from '../scheduleConflict';
import type { ConflictResult } from '../scheduleConflict';

function d10(d: string | null | undefined): string { if (!d) return ''; return d.slice(0, 10); }

type Mode = 'list' | 'register' | 'result' | 'confirm' | 'detail';
const DAYS = ['일','월','화','수','목','금','토'];
const FP_COMPANIES_FALLBACK = ['삼성생명','한화생명','교보생명','NH농협생명','신한라이프','메트라이프','푸르덴셜','미래에셋생명','DB손해보험','현대해상','KB손해보험','삼성화재','메리츠화재','한화손해보험','기타'];

interface FormState {
  fp_id: string; fp_name_input: string; fp_company: string; fp_phone: string;
  /** FP 없이 WM 님이 FP 역할(2026-10-27 지도사 「FP가 없고 WM님이 FP 역할을 하는 경우들도 있어」) — FP 칸 필수 해제 · 결과는 WM 님께 */
  fp_none: boolean;
  company_id: string; biz_no: string; company_name: string; ceo: string; ceo_phone: string;
  /** 실제 대표(명의 대표와 다를 때 · 2026-10-18) */
  real_on: boolean; real_ceo: string; real_ceo_phone: string;
  date: string; time: string; location: string; topic: string; notes: string;
  confirm_status: 'confirmed' | 'tentative';
  candidate_slots: CandidateSlot[];
  fee_type: 'paid' | 'free'; fee_method: 'individual' | 'fa_settle' | '';
  fee_amount: string; fee_memo: string;
  /** 함께하는 WM 여러 명(첫 분 대표 · 공동관리 · 2026-10-26) · wm_none = 「없음」을 고름 */
  wm_ids: string[]; wm_none: boolean;
  ref_files: File[];
}
const EMPTY_FORM: FormState = {
  fp_id:'', fp_name_input:'', fp_company:'', fp_phone:'', fp_none:false,
  company_id:'', biz_no:'', company_name:'', ceo:'', ceo_phone:'', real_on:false, real_ceo:'', real_ceo_phone:'',
  date:'', time:'', location:'', topic:'', notes:'',
  confirm_status:'confirmed', candidate_slots:[],
  fee_type:'paid', fee_method:'individual', fee_amount:'100000', fee_memo:'',
  wm_ids:[], wm_none:false,
  ref_files:[] as File[],
};

interface Props { store: TMStore; onNavigate?: (section: string, params?: unknown) => void }

export default function ConsultationPage({ store, onNavigate }: Props) {
  const { consultations, companies, fps, wms, taskTemplates } = store;
  const todayStr = today();
  const [mode, setMode] = useState<Mode>('list');
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState(() => new Date().getMonth());
  const [selDate, setSelDate] = useState<string>(todayStr);
  const [selId, setSelId] = useState<string | null>(null);
  const [toast, setToast] = useState('');
  const [search, setSearch] = useState('');
  const [holidays, setHolidays] = useState<Record<string,string>>({});
  const [form, setForm] = useState<FormState>({...EMPTY_FORM});
  const [bizLoading, setBizLoading] = useState(false);
  const [fpSuggestions, setFpSuggestions] = useState<FP[]>([]);
  const [showFpDrop, setShowFpDrop] = useState(false);
  const [insuranceCompanies, setInsuranceCompanies] = useState<string[]>(FP_COMPANIES_FALLBACK);
  useEffect(() => { api.getInsuranceCompanies().then(r => { const names = (r.data || []).map((c: InsuranceCompany) => c.name); if (names.length > 0) setInsuranceCompanies(names); }).catch(() => {}); }, []);
  const [existingCompanyMatch, setExistingCompanyMatch] = useState(false);
  const [bizLookupData, setBizLookupData] = useState<Record<string,string|number|null>>({});
  const [confirmDeleteId, setConfirmDeleteId] = useState<string|null>(null);
  const [postRegConsult, setPostRegConsult] = useState<Consultation|null>(null);
  const [postRegSmsPreview, setPostRegSmsPreview] = useState(false);
  const [postRegSmsText, setPostRegSmsText] = useState('');
  const [postRegSmsSending, setPostRegSmsSending] = useState(false);
  const upd = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm(p => ({...p, [k]: v}));
  // 강의 스케줄 — 같은 달력에 함께 보여주고, 시간 겹침을 막는다 (2026-09-28)
  const [lectures, setLectures] = useState<Lecture[]>([]);
  useEffect(() => { lecApi.getLectures({ period: 'all' }).then(r => { if (r.success && r.data) setLectures(r.data); }).catch(() => {}); }, []);
  const busyAll = useMemo(() => [...consultBusy(consultations), ...lectureBusy(lectures)], [consultations, lectures]);
  const clash = useMemo(() => clashIds(busyAll), [busyAll]);
  const lecByDate = useMemo(() => {
    const m: Record<string, Lecture[]> = {};
    lectures.forEach(l => { if (l.lecture_date && l.status !== 'canceled') (m[l.lecture_date] = m[l.lecture_date] || []).push(l); });
    return m;
  }, [lectures]);
  const openLecture = (id: string) => onNavigate?.('lectures', { lectureId: id });
  // 등록 폼의 날짜·시간이 다른 일정과 겹치는지 (확정 = 날짜/시간, 미정 = 후보 일정 각각)
  const formConflicts: { date: string; time: string; r: ConflictResult }[] = useMemo(() => {
    const slots = form.confirm_status === 'confirmed' ? [{ date: form.date, time: form.time }] : form.candidate_slots.map(sl => ({ date: sl.date, time: sl.time }));
    return slots.filter(x => x.date && x.time).map(x => ({ ...x, r: findConflicts(busyAll, x.date, x.time, '', CONSULT_MIN) })).filter(x => x.r.overlaps.length > 0);
  }, [form.confirm_status, form.date, form.time, form.candidate_slots, busyAll]);

  useEffect(() => {
    api.getHolidays(year).then(res => {
      const map: Record<string,string> = {};
      (res.data || []).forEach(h => { map[h.date] = h.name; });
      setHolidays(map);
    }).catch(() => {});
  }, [year]);

  const selDateConsults = consultations.filter(c => d10(c.date) === selDate);
  const sel = consultations.find(c => c.id === selId);

  const calDays = useMemo(() => {
    const first = new Date(year, month, 1);
    const last = new Date(year, month+1, 0);
    const days: (null | { day: number; date: string; dow: number; consults: Consultation[]; holiday: string|null })[] = [];
    for (let i = 0; i < first.getDay(); i++) days.push(null);
    for (let d = 1; d <= last.getDate(); d++) {
      const ds = `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
      // #9 fix: 배지 카운트 = 하단 상세 리스트와 동일 필터(c.date 매칭만)
      const dayConsults = consultations.filter(c => d10(c.date) === ds);
      days.push({ day: d, date: ds, dow: new Date(year,month,d).getDay(),
        consults: dayConsults, holiday: holidays[ds] || null });
    }
    return days;
  }, [year, month, consultations, holidays]);

  function prevMonth() { if (month===0) {setMonth(11);setYear(y=>y-1);} else setMonth(m=>m-1); }
  function nextMonth() { if (month===11) {setMonth(0);setYear(y=>y+1);} else setMonth(m=>m+1); }
  function showToast(msg: string) { setToast(msg); setTimeout(() => setToast(''), 3000); }

  // 상담 삭제 (DB에서 완전 삭제)
  async function deleteConsult(id: string) {
    try {
      await api.deleteConsultation(id);
      await store.refresh();
      setConfirmDeleteId(null);
      if (selId === id) setSelId(null);
      showToast('삭제되었습니다');
    } catch (e: unknown) { showToast(e instanceof Error ? e.message : '삭제 실패'); }
  }

  function buildConsultSmsText(c: Consultation): string {
    const fp = fps.find(f => f.id === c.fp_id) || fps.find(f => f.name === c.fp_name);
    const portalUrl = fp?.portal_token ? `${window.location.origin}/fp/${fp.portal_token}` : '';
    return [
      `[임지도사] ${c.company_name || '업체'} 동행상담이 확정되었습니다.`,
      '',
      `일시: ${d10(c.date)} ${c.time || ''}`,
      `장소: ${c.location || '미정'}`,
      ...(portalUrl ? [`포털: ${portalUrl}`] : []),
    ].join('\n');
  }

  async function handlePostRegSms() {
    if (!postRegConsult) return;
    const phone = postRegConsult.fp_phone || fps.find(f => f.id === postRegConsult.fp_id)?.phone || '';
    if (!phone) { showToast('FP 연락처가 없습니다'); return; }
    setPostRegSmsSending(true);
    try {
      await api.sendSms({ to: phone, message: postRegSmsText, context: 'consult-confirm', company_id: postRegConsult.company_id, reference_id: postRegConsult.id });
      showToast('SMS가 발송되었습니다');
      setPostRegSmsPreview(false); setPostRegConsult(null);
    } catch (e: unknown) { showToast(e instanceof Error ? e.message : 'SMS 발송 실패'); }
    finally { setPostRegSmsSending(false); }
  }

  // 카드 클릭 → 상태별 모달
  function handleCardClick(c: Consultation) {
    setSelId(c.id);
    if (c.confirm_status === 'tentative' && c.status === 'scheduled') {
      setMode('confirm');
    } else {
      setMode('detail');
    }
  }

  // 상태 뱃지 계산
  function getStatusBadge(c: Consultation) {
    if (c.confirm_status === 'tentative' && c.status === 'scheduled') {
      return { label: '미정', bg: '#FFFBEB', color: '#D97706' };
    }
    if (c.status === 'completed' || c.status === 'sent') {
      return { label: '완료', bg: C.bg, color: C.muted };
    }
    if (c.status === 'scheduled' && c.confirm_status !== 'tentative') {
      if (d10(c.date) <= todayStr) {
        return { label: '결과미입력', bg: '#FEF2F2', color: '#DC2626' };
      }
      return { label: '확정', bg: '#F0FDF4', color: '#16A34A' };
    }
    return { label: c.status, bg: C.bg, color: C.muted };
  }

  function onFpNameChange(v: string) {
    upd('fp_name_input', v);
    if (v.length > 0) {
      const matched = fps.filter(f => f.name.includes(v));
      setFpSuggestions(matched); setShowFpDrop(matched.length > 0);
    } else { setShowFpDrop(false); }
    upd('fp_id', '');
  }
  function onFpPhoneChange(v: string) {
    upd('fp_phone', v);
    if (v.replace(/\D/g,'').length >= 10) {
      const matched = fps.find(f => f.phone && f.phone.replace(/\D/g,'') === v.replace(/\D/g,''));
      if (matched) {
        setForm(p => ({...p, fp_id: matched.id, fp_name_input: matched.name, fp_company: matched.company||'', fp_phone: v}));
      }
    }
  }
  function selectFp(fp: FP) {
    setForm(p => ({...p, fp_id: fp.id, fp_name_input: fp.name, fp_company: fp.company||'', fp_phone: fp.phone||''}));
    setShowFpDrop(false);
  }

  async function lookupBiz() {
    const cleaned = form.biz_no.replace(/\D/g,'');
    if (cleaned.length !== 10) { showToast('사업자번호 10자리를 입력하세요'); return; }
    setBizLoading(true);
    try {
      const res = await api.bizLookup(cleaned);
      const d = res.data;
      if (d && d.name) {
        let existing = companies.find(c => (c.biz_no || '').replace(/\D/g,'') === cleaned);
        if (!existing) {
          // 스토어는 최대 500건만 로드 — 미탑재 업체는 서버에서 직접 조회
          try {
            const sr = await api.getCompanies({ search: cleaned, limit: 5 });
            existing = (sr.data?.companies || []).find(c => (c.biz_no || '').replace(/\D/g,'') === cleaned);
          } catch { /* 서버 조회 실패 시 신규 등록 플로우로 진행 */ }
        }
        if (existing) {
          setForm(p => ({...p, company_id: existing.id, company_name: existing.name||d.name, ceo: existing.ceo||d.ceo||p.ceo, ceo_phone: existing.ceo_phone||p.ceo_phone,
            ...(existing.real_ceo || existing.real_ceo_phone ? { real_on: true, real_ceo: existing.real_ceo || '', real_ceo_phone: existing.real_ceo_phone || '' } : {})}));
          setExistingCompanyMatch(true);
          showToast(`기존 업체: ${existing.name||d.name}`);
        } else {
          setForm(p => ({...p, company_name: d.name, ceo: d.ceo||p.ceo}));
          setBizLookupData({ company_reg_num: d.company_reg_num||'', biz_type: d.biz_class||d.biz_type_detail||'', address: (d.address||'').trim(), established_year: d.established_year||null });
          setExistingCompanyMatch(false);
          showToast(`${d.name} · ${d.ceo}`);
        }
      } else { showToast('조회 결과 없음'); }
    } catch { showToast('조회 실패'); }
    finally { setBizLoading(false); }
  }

  function formatBizNo(v: string) {
    const n = v.replace(/\D/g,'').slice(0,10);
    if (n.length <= 3) return n; if (n.length <= 5) return n.slice(0,3)+'-'+n.slice(3);
    return n.slice(0,3)+'-'+n.slice(3,5)+'-'+n.slice(5);
  }

  function addSlot() {
    const firstLoc = form.candidate_slots.length > 0 ? form.candidate_slots[0].location : '';
    upd('candidate_slots', [...form.candidate_slots, {date:'',time:'',location:firstLoc}]);
  }
  function updSlot(idx: number, key: keyof CandidateSlot, val: string) {
    upd('candidate_slots', form.candidate_slots.map((s,i) => i===idx ? {...s,[key]:val} : s));
  }
  function removeSlot(idx: number) { upd('candidate_slots', form.candidate_slots.filter((_,i) => i!==idx)); }

  // 등록 — 두 번 눌러도 한 번만(2026-10-18 지도사 「등록되는 속도가 느려 · 클릭 두 번 하니까 두 번 등록」) · 서버도 1분 안 같은 약속은 하나로
  const regRef = useRef(false);
  const [regBusy, setRegBusy] = useState(false);
  // 겹치는 일정 — 브라우저 확인 창 대신 화면 안에서 한 번 더 누르게(같은 날짜 · 시각일 때만 그대로 둔다)
  const clashSig = formConflicts.map(x => `${x.date} ${x.time}`).join('|');
  const [clashOk, setClashOk] = useState('');
  async function handleRegister() {
    if (regRef.current) return;
    if (formConflicts.length > 0 && clashOk !== clashSig) { setClashOk(clashSig); return; }
    regRef.current = true; setRegBusy(true);
    try { await doRegister(); } finally { regRef.current = false; setRegBusy(false); }
  }
  async function doRegister() {
    if (form.fp_none) {
      if (!form.wm_ids.length) { showToast('FP 없이 진행하면 FP 역할을 할 WM 님을 골라 주세요'); return; }
    } else {
      if (!form.fp_id && !form.fp_name_input.trim()) { showToast('FP명을 입력하세요 (필수) — FP 없이 WM 님이 맡으면 「FP 없음」을 눌러 주세요'); return; }
      if (!form.fp_id && !form.fp_company) { showToast('FP 소속을 선택하세요 (필수)'); return; }
    }
    if (!form.wm_ids.length && !form.wm_none) { showToast('담당 WM을 선택하세요 — 없으면 "없음"을 골라주세요'); return; }
    if (!form.company_name.trim()) { form.company_name = (form.fp_none ? (wms.find(w => w.id === form.wm_ids[0])?.name || 'WM') : (form.fp_name_input.trim() || 'FP')) + ' 고객'; }
    if (form.confirm_status === 'confirmed' && !form.date) { showToast('날짜를 입력하세요'); return; }
    if (form.confirm_status === 'tentative' && form.candidate_slots.filter(s => s.date).length === 0) { showToast('후보 일정 1개 이상 입력'); return; }
    try {
      // "없음"을 고른 경우와 안 고른 경우 모두 DB 에는 null 로 들어간다
      const wmIds = form.wm_none ? [] : form.wm_ids;
      const wmId = wmIds[0] || null;   // 대표 WM — FP · 새 업체의 담당 WM 은 대표 한 분
      let fpId = form.fp_none ? '' : form.fp_id;
      // 다시 읽기는 끝에 한 번 · 바뀐 것만(예전엔 전체 store.refresh 를 서너 번 — 업체 · 공고 · 업무 전부라 등록이 느렸다)
      let fpChanged = false, coChanged = false;
      if (!form.fp_none && !fpId && form.fp_name_input.trim()) {
        try { const r = await api.createFp({ name: form.fp_name_input.trim(), company: form.fp_company, phone: form.fp_phone, wm_id: wmId }); fpId = r.data?.id || ''; fpChanged = true; } catch { /* FP 자동생성 실패 시 계속 진행 */ }
      }
      // 기존 FP — 여기서 고른 소속 · 비어 있던 연락처 · 담당 WM 을 FP 정보에도 (2026-10-18 지도사 「지정이 안 되어 있어서 내가 여기서 지정하면 FP 정보에 자동 업데이트」)
      //   소속은 보험사 색(팀장님 포털 · 고객 안내)을 정한다 · 연락처 · WM 은 비어 있을 때만 채운다(있던 값은 FP 관리에서 고침)
      if (fpId && form.fp_id === fpId) {
        const cur = fps.find(f => f.id === fpId);
        const patch: Partial<FP> = {};
        const co = form.fp_company.trim(), ph = form.fp_phone.trim();
        if (cur && co && co !== (cur.company || '').trim()) patch.company = co;
        if (cur && ph && !(cur.phone || '').trim()) patch.phone = ph;
        if (cur && wmId && !cur.wm_id) patch.wm_id = wmId;
        if (Object.keys(patch).length) {
          try { await api.updateFp(fpId, patch); fpChanged = true; }
          catch (e: unknown) { showToast('FP 정보 저장 실패: ' + (e instanceof Error ? e.message : '알 수 없는 오류') + ' — 상담 등록은 계속해요'); }
        }
      }
      let companyId = form.company_id;
      if (!companyId && form.company_name.trim()) {
        try {
          const realOf: { real_ceo?: string; real_ceo_phone?: string } = form.real_on && (form.real_ceo.trim() || form.real_ceo_phone.trim()) ? { real_ceo: form.real_ceo.trim(), real_ceo_phone: form.real_ceo_phone.trim() } : {};
          const r = await api.createCompanyOrLink({ name: form.company_name.trim(), biz_no: form.biz_no.replace(/\D/g,''), ceo: form.ceo, ceo_phone: form.ceo_phone, tm_status: '대기' as const, source: '상담등록', wm_ids: wmIds, ...bizLookupData, ...realOf });
          if (r.existed && realOf.real_ceo !== undefined) { try { await api.updateCompany(r.id, realOf); } catch { /* 실제 대표는 업체 상세에서 다시 */ } }
          companyId = r.id;
          if (r.existed) showToast(`기존 업체에 연결: ${r.existingName || form.company_name.trim()}`);
          coChanged = true;
        } catch (e: unknown) { showToast(e instanceof Error ? e.message : '업체 등록 실패'); return; }
      }
      if (!companyId) { showToast('업체 정보 오류'); return; }
      // 이미 있는 업체를 고른 경우 — 여기서 적은 실제 대표를 업체 정보에 (2026-10-18)
      if (form.company_id && form.real_on && (form.real_ceo.trim() || form.real_ceo_phone.trim())) {
        try { await api.updateCompany(form.company_id, { real_ceo: form.real_ceo.trim(), real_ceo_phone: form.real_ceo_phone.trim() }); }
        catch { showToast('실제 대표 저장 실패 — 업체 상세에서 다시 넣어 주세요'); }
      }
      const res = await api.createConsultation({
        company_id: companyId, fp_id: fpId || null,
        date: form.confirm_status === 'confirmed' ? form.date : (form.candidate_slots[0]?.date||''),
        time: form.confirm_status === 'confirmed' ? form.time : '',
        location: form.location, topic: form.topic, notes: form.notes,
        confirm_status: form.confirm_status,
        candidate_slots: form.confirm_status === 'tentative' ? form.candidate_slots.filter(s => s.date) : [],
        fee_type: form.fee_type, fee_method: form.fee_type === 'paid' ? (form.fee_method||'individual') : null,
        fee_amount: form.fee_type === 'paid' ? parseInt(form.fee_amount)||100000 : 0, fee_memo: form.fee_memo,
        wm_id: wmId, wm_ids: wmIds,
      } as unknown as Partial<Consultation>);
      // 첨부가 있으면 올린 뒤에 한 번만 다시 읽는다
      // 사전 첨부파일 업로드 (if/else 분기 전에 실행)
      if (form.ref_files.length > 0 && res.data?.id) {
        const fd = new FormData();
        form.ref_files.forEach(f => fd.append('files', f));
        const token = localStorage.getItem('token') || '';
        await fetch('/api/tm/consultations/' + res.data.id + '/ref-files', {
          method: 'POST', headers: { Authorization: 'Bearer ' + token }, body: fd,
        });
      }
      await Promise.all([store.refreshConsultations(), fpChanged ? store.refreshFps() : null, coChanged ? store.refreshCompanies() : null]);
      // Find newly created consultation for post-reg dialog
      const newConsult: Consultation = {
        ...(res.data || {}),
        company_name: form.company_name.trim(),
        fp_name: form.fp_name_input.trim(),
        fp_phone: form.fp_phone.trim(),
        fp_id: fpId || form.fp_id,
        date: form.confirm_status === 'confirmed' ? form.date : (form.candidate_slots[0]?.date||''),
        time: form.confirm_status === 'confirmed' ? form.time : '',
        location: form.location,
      } as Consultation;
      setMode('list'); setForm({...EMPTY_FORM}); setExistingCompanyMatch(false);
      if (newConsult.id && !form.fp_none && form.fp_phone.trim()) {
        setPostRegConsult(newConsult);
      } else {
        showToast('상담이 등록되었습니다');
      }
    } catch (e: unknown) { showToast(e instanceof Error ? e.message : '등록 실패'); }
  }

  // ═══ 상담 결과 조회 모달 (PeekStack) ═══
  if (mode === 'detail' && sel) return (
    <ConsultPeekFlow consultation={sel}
      fps={store.fps} wms={store.wms}
      templates={taskTemplates} taskCategories={store.taskCategories} aiPresets={store.aiPresets}
      onDone={async () => { await store.refresh(); setMode('list'); setSelId(null); }}
      onCancel={() => { setMode('list'); setSelId(null); }}
      onNavigate={onNavigate}
      onToast={showToast} />
  );

  // ═══ 일정 확정 모달 ═══
  if (mode === 'confirm' && sel) return (
    <ConsultConfirmModal consultation={sel}
      onDone={async () => { await store.refresh(); setMode('list'); setSelId(null); }}
      onCancel={() => { setMode('list'); setSelId(null); }} onToast={showToast} />
  );

  // ═══ 등록 모달 ═══
  if (mode === 'register') return (
    <div style={{position:'fixed', inset:0, zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center'}}>
      <div onClick={() => setMode('list')} style={{position:'absolute', inset:0, background:'rgba(0,0,0,0.4)'}} />
      <div style={{position:'relative', width:560, maxHeight:'90vh', background:C.white, borderRadius:16, display:'flex', flexDirection:'column', boxShadow:'0 8px 32px rgba(0,0,0,0.15)'}}>
        <div style={{padding:'14px 20px', background:'linear-gradient(135deg, #111827, #1B3A5C)', borderRadius:'12px 12px 0 0', display:'flex', alignItems:'center', gap:10}}>
          <span style={{fontSize:T.lg, fontWeight:700, flex:1, color:'#fff'}}>🤝 동행상담 약속 등록</span>
          <button onClick={() => setMode('list')} style={{width:28, height:28, borderRadius:6, border:'1px solid rgba(255,255,255,0.3)', background:'rgba(255,255,255,0.15)', cursor:'pointer', fontSize:T.md, color:'#fff'}}>✕</button>
        </div>
        <div style={{flex:1, overflowY:'auto', padding:'16px 20px'}}>
          {/* WM 정보 */}
          <div>
            <div style={{fontSize:13, fontWeight:700, color:'#1B3A5C', paddingBottom:8, borderBottom:'2px solid #E5E7EB', marginBottom:12}}>🧑‍💼 담당 WM <span style={{color:'#EF4444', fontSize:11}}>* 필수</span><span style={{fontWeight:400, color:C.sub, fontSize:11, marginLeft:8}}>함께 맡는 분이 있으면 더 고르세요</span></div>
            {/* 이름으로 찾고 없으면 바로 등록 — 여러 명(공동관리 · 2026-10-26) · 첫 분이 대표 */}
            <WmMultiPicker people={wms} value={form.wm_ids} onChange={ids => setForm(p => ({...p, wm_ids: ids, wm_none: false}))}
              none={form.wm_none} onNone={on => setForm(p => ({...p, wm_none: on, wm_ids: on ? [] : p.wm_ids}))}
              onCreated={() => { store.refresh().catch(() => { /* 목록 갱신 실패해도 칸이 막 등록한 분을 들고 있다 */ }); }} onToast={showToast} />
          </div>
          {/* FP 정보 */}
          <div style={{paddingTop:24, borderTop:'1px solid #F3F4F6'}}>
            <div style={{fontSize:13, fontWeight:700, color:'#1B3A5C', paddingBottom:8, borderBottom:'2px solid #E5E7EB', marginBottom:12}}>👤 FP 정보</div>
            {/* FP 없이 WM 님이 FP 역할 — WM 칸 「없음」과 같은 모양(2026-10-27) */}
            {form.fp_none ? (
              <div style={{display:'flex', alignItems:'center', gap:8, minHeight:36, padding:'0 10px', borderRadius:R.sm, border:`1px solid ${C.border}`, background:C.bg}}>
                <span style={{flex:1, minWidth:0, fontSize:T.sm, color:C.sub}}>FP 없음 — {wms.find(w => w.id === form.wm_ids[0])?.name || '담당 WM'} 님이 FP 역할</span>
                <button type="button" onClick={() => upd('fp_none', false)} style={{height:26, padding:'0 10px', borderRadius:R.sm, border:`1px solid ${C.border}`, background:'#fff', color:C.sub, fontSize:T.xs, cursor:'pointer', whiteSpace:'nowrap'}}>FP 입력</button>
              </div>
            ) : <>
            <div style={{position:'relative', marginBottom:8}}>
              <label style={{fontSize:11, fontWeight:600, color:'#6B7280', display:'block', marginBottom:4}}>FP명 <span style={{color:'#EF4444', fontSize:11}}>* 필수</span></label>
              <input value={form.fp_name_input} onChange={e => onFpNameChange(e.target.value)} onFocus={() => { if (form.fp_name_input && fpSuggestions.length) setShowFpDrop(true); }} placeholder="이름 입력 (기존 FP 자동완성)"
                style={{width:'100%', padding:'9px 12px', height:38, borderRadius:8, border:`1px solid ${C.border}`, fontSize:T.md, outline:'none', boxSizing:'border-box'}} />
              {showFpDrop && fpSuggestions.length > 0 && (
                <div style={{position:'absolute', top:'100%', left:0, right:0, zIndex:10, background:C.white, border:`1px solid ${C.border}`, borderRadius:8, boxShadow:'0 4px 12px rgba(0,0,0,0.1)', maxHeight:150, overflowY:'auto'}}>
                  {fpSuggestions.map(fp => (<div key={fp.id} onClick={() => selectFp(fp)} style={{padding:'8px 12px', cursor:'pointer', fontSize:T.sm, borderBottom:`1px solid ${C.bg}`}}><span style={{fontWeight:600}}>{fp.name}</span><span style={{color:C.muted, marginLeft:8}}>{fp.company||''} · {fp.phone||''}</span></div>))}
                </div>
              )}
            </div>
            <div style={{display:'flex', gap:8}}>
              <div style={{flex:1}}><label style={{fontSize:11, fontWeight:600, color:'#6B7280', display:'block', marginBottom:4}}>소속 <span style={{color:'#EF4444', fontSize:11}}>* 필수</span></label>
                <select value={form.fp_company} onChange={e => upd('fp_company', e.target.value)} style={{width:'100%', padding:'9px 12px', height:38, borderRadius:8, border:`1px solid ${C.border}`, fontSize:T.md, outline:'none', background:'#fff', boxSizing:'border-box'}}>
                  <option value="" disabled>선택하세요</option>
                  {/* 실제 등록된 소속을 건수 많은 순으로 먼저 — 기본 목록에 없는 표기(예: 한화생명금융서비스)가 빠지면 표기가 갈린다 */}
                  {(() => {
                    const cnt = new Map<string, number>();
                    fps.forEach(f => { const v = (f.company || '').trim(); if (v) cnt.set(v, (cnt.get(v) || 0) + 1); });
                    const used = Array.from(cnt.entries()).sort((a, b) => b[1] - a[1]).map(e => e[0]);
                    return Array.from(new Set([...used, ...insuranceCompanies])).map(c => <option key={c} value={c}>{c}</option>);
                  })()}
                </select></div>
              <div style={{flex:1}}><label style={{fontSize:11, fontWeight:600, color:'#6B7280', display:'block', marginBottom:4}}>FP 휴대폰번호 <span style={{color:'#EF4444', fontSize:11}}>* 필수</span></label>
                <input value={form.fp_phone} onChange={e => onFpPhoneChange(typingPhone(e.target.value))} placeholder="010-" style={{width:'100%', padding:'9px 12px', height:38, borderRadius:8, border:`1px solid ${C.border}`, fontSize:T.md, outline:'none', boxSizing:'border-box'}} /></div>
            </div>
            <button type="button" onClick={() => setForm(p => ({...p, fp_none: true, fp_id: '', fp_name_input: '', fp_company: '', fp_phone: ''}))}
              style={{marginTop:8, height:26, padding:'0 10px', borderRadius:R.sm, border:`1px solid ${C.border}`, background:'#fff', color:C.sub, fontSize:T.xs, cursor:'pointer'}}>FP 없음 — WM 님이 FP 역할</button>
            </>}
          </div>
          {/* 업체 정보 */}
          <div style={{paddingTop:24, borderTop:'1px solid #F3F4F6'}}>
            <div style={{fontSize:13, fontWeight:700, color:'#1B3A5C', paddingBottom:8, borderBottom:'2px solid #E5E7EB', marginBottom:12}}>🏢 업체 정보 {existingCompanyMatch && <span style={{fontSize:T.xs, fontWeight:600, padding:'2px 8px', borderRadius:8, background:'#DCFCE7', color:'#16A34A', marginLeft:8}}>기존 업체 연결</span>}</div>
            <div style={{marginBottom:8}}>
              <label style={{fontSize:11, fontWeight:600, color:'#6B7280', display:'block', marginBottom:4}}>사업자번호</label>
              <div style={{display:'flex', gap:6}}>
              <input value={form.biz_no} onChange={e => { upd('biz_no', formatBizNo(e.target.value)); setExistingCompanyMatch(false); upd('company_id',''); }} placeholder="000-00-00000" style={{flex:1, padding:'9px 12px', height:38, borderRadius:8, border:`1px solid ${C.border}`, fontSize:T.md, outline:'none'}} />
              <Btn onClick={lookupBiz} disabled={bizLoading} style={{fontSize:T.sm, padding:'8px 14px', whiteSpace:'nowrap' as const}}>{bizLoading ? '⏳' : '🔍 조회'}</Btn>
              </div>
            </div>
            <div style={{marginBottom:8}}><label style={{fontSize:11, fontWeight:600, color:'#6B7280', display:'block', marginBottom:4}}>업체명 <span style={{color:'#EF4444', fontSize:11}}>* 필수</span></label>
              <input value={form.company_name} onChange={e => upd('company_name', e.target.value)} placeholder="업체명" style={{width:'100%', padding:'9px 12px', height:38, borderRadius:8, border:`1px solid ${form.company_name?'#16A34A':C.border}`, fontSize:T.md, outline:'none', boxSizing:'border-box', fontWeight:form.company_name?600:400}} /></div>
            <div style={{display:'flex', gap:8}}>
              <div style={{flex:1}}><label style={{fontSize:11, fontWeight:600, color:'#6B7280', display:'block', marginBottom:4}}>대표자명</label><input value={form.ceo} onChange={e => upd('ceo', e.target.value)} placeholder="대표자명" style={{width:'100%', padding:'9px 12px', height:38, borderRadius:8, border:`1px solid ${C.border}`, fontSize:T.md, outline:'none', boxSizing:'border-box'}} /></div>
              <div style={{flex:1}}><label style={{fontSize:11, fontWeight:600, color:'#6B7280', display:'block', marginBottom:4}}>연락처</label><input value={form.ceo_phone} onChange={e => upd('ceo_phone', typingPhone(e.target.value))} placeholder="010-" style={{width:'100%', padding:'9px 12px', height:38, borderRadius:8, border:`1px solid ${C.border}`, fontSize:T.md, outline:'none', boxSizing:'border-box'}} /></div>
            </div>
            {/* 실제 대표 — 명의 대표와 다를 때만 (2026-10-18 지도사 「명의상 대표 말고 실제 대표가 따로 있는 경우」) */}
            {form.real_on ? (
              <div style={{display:'flex', gap:8, marginTop:8}}>
                <div style={{flex:1}}><label style={{fontSize:11, fontWeight:600, color:'#6B7280', display:'block', marginBottom:4}}>실제 대표</label><input value={form.real_ceo} onChange={e => upd('real_ceo', e.target.value)} placeholder="실제 대표 이름" style={{width:'100%', padding:'9px 12px', height:38, borderRadius:8, border:`1px solid ${C.border}`, fontSize:T.md, outline:'none', boxSizing:'border-box'}} /></div>
                <div style={{flex:1}}><label style={{fontSize:11, fontWeight:600, color:'#6B7280', display:'flex', justifyContent:'space-between', marginBottom:4}}><span>실제 대표 연락처</span>
                  <button type="button" onClick={() => setForm(p => ({...p, real_on:false, real_ceo:'', real_ceo_phone:''}))} style={{border:'none', background:'none', padding:0, fontSize:11, color:C.muted, cursor:'pointer'}}>빼기</button></label>
                  <input value={form.real_ceo_phone} onChange={e => upd('real_ceo_phone', typingPhone(e.target.value))} placeholder="010-" style={{width:'100%', padding:'9px 12px', height:38, borderRadius:8, border:`1px solid ${C.border}`, fontSize:T.md, outline:'none', boxSizing:'border-box'}} /></div>
              </div>
            ) : (
              <button type="button" onClick={() => upd('real_on', true)}
                style={{marginTop:8, height:30, padding:'0 12px', borderRadius:15, border:`1px dashed ${C.accent}`, background:C.white, color:C.accent, fontSize:T.xs, fontWeight:700, cursor:'pointer'}}>+ 실제 대표가 따로 있어요</button>
            )}
          </div>
          {/* 일정 */}
          <div style={{paddingTop:24, borderTop:'1px solid #F3F4F6'}}>
            <div style={{fontSize:13, fontWeight:700, color:'#1B3A5C', paddingBottom:8, borderBottom:'2px solid #E5E7EB', marginBottom:12}}>📅 일정</div>
            <div style={{display:'flex', gap:8, marginBottom:10}}>
              <button onClick={() => upd('confirm_status','confirmed')} style={{flex:1, padding:'8px 0', borderRadius:10, fontSize:T.sm, fontWeight:600, cursor:'pointer', border:`2px solid ${form.confirm_status==='confirmed'?'#16A34A':C.border}`, background:form.confirm_status==='confirmed'?'#DCFCE7':'#fff', color:form.confirm_status==='confirmed'?'#16A34A':C.sub}}>● 확정</button>
              <button onClick={() => upd('confirm_status','tentative')} style={{flex:1, padding:'8px 0', borderRadius:10, fontSize:T.sm, fontWeight:600, cursor:'pointer', border:`2px solid ${form.confirm_status==='tentative'?'#D97706':C.border}`, background:form.confirm_status==='tentative'?'#FEF3C7':'#fff', color:form.confirm_status==='tentative'?'#D97706':C.sub}}>● 미정</button>
            </div>
            {form.confirm_status === 'confirmed' && (<>
              <div style={{display:'flex', gap:8}}><div style={{flex:1}}><Field label="날짜" value={form.date} type="date" required onChange={v => upd('date',v)} /></div><div style={{flex:1}}>
                <div style={{fontSize:11, fontWeight:600, color:C.muted, marginBottom:4}}>시간</div>
                <select value={form.time} onChange={e => upd('time', e.target.value)} style={{width:'100%', padding:'9px 12px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:13, outline:'none', background:'#fff', boxSizing:'border-box' as const, height:38}}>
                  <option value="">시간 선택</option>
                  {Array.from({length:28}, (_,i) => { const h=Math.floor(i/2)+7; const m=i%2===0?'00':'30'; const t=`${String(h).padStart(2,'0')}:${m}`; const ampm=h<12?'오전':h===12?'오후':'오후'; const dh=h<=12?h:h-12; return <option key={t} value={t}>{ampm} {dh}:{m}</option>; })}
                </select>
              </div></div>
              <ConflictNote items={formConflicts} />
              <Field label="장소" value={form.location} placeholder="강남역 스타벅스" onChange={v => upd('location',v)} />
            </>)}
            {form.confirm_status === 'tentative' && (
              <div>
                {form.candidate_slots.map((slot,i) => (
                  <div key={i} style={{display:'flex', gap:4, marginBottom:4, alignItems:'center'}}>
                    <span style={{fontSize:T.sm, color:C.muted, width:20, textAlign:'center'}}>{i+1}</span>
                    <input type="date" value={slot.date} onChange={e => updSlot(i,'date',e.target.value)} style={{flex:1, padding:'9px 12px', height:38, borderRadius:8, border:`1px solid ${C.border}`, fontSize:T.md, outline:'none', boxSizing:'border-box'}} />
                    <select value={slot.time} onChange={e => updSlot(i,'time',e.target.value)} style={{width:110, padding:'9px 8px', height:38, borderRadius:8, border:`1px solid ${C.border}`, fontSize:T.md, outline:'none', boxSizing:'border-box' as const, background:'#fff'}}>
                      <option value="">시간</option>
                      {Array.from({length:28}, (_,ii) => { const h=Math.floor(ii/2)+7; const m=ii%2===0?'00':'30'; const t=`${String(h).padStart(2,'0')}:${m}`; const ap=h<12?'오전':h===12?'오후':'오후'; const dh=h<=12?h:h-12; return <option key={t} value={t}>{ap} {dh}:{m}</option>; })}
                    </select>
                    <input value={slot.location} onChange={e => updSlot(i,'location',e.target.value)} placeholder="장소" style={{flex:1, padding:'9px 12px', height:38, borderRadius:8, border:`1px solid ${C.border}`, fontSize:T.md, outline:'none', boxSizing:'border-box'}} />
                    <button onClick={() => removeSlot(i)} style={{background:'none', border:'none', color:C.red, cursor:'pointer', fontSize:T.lg}}>✕</button>
                  </div>
                ))}
                <ConflictNote items={formConflicts} />
                <button onClick={addSlot} style={{width:'100%', padding:10, borderRadius:10, border:`2px dashed ${C.border}`, background:'#fff', color:C.sub, fontSize:T.sm, fontWeight:600, cursor:'pointer'}}>+ 후보 일정 추가</button>
              </div>
            )}
          </div>
          {/* 상담료 */}
          <div style={{paddingTop:24, borderTop:'1px solid #F3F4F6'}}>
            <div style={{fontSize:13, fontWeight:700, color:'#1B3A5C', paddingBottom:8, borderBottom:'2px solid #E5E7EB', marginBottom:12}}>💰 상담료</div>
            <div style={{display:'flex', gap:6, marginBottom:8}}>
              {([['paid','💰 지불'],['free','🎁 면제']] as const).map(([k,l]) => (
                <button key={k} onClick={() => upd('fee_type',k)} style={{flex:1, padding:'8px 0', borderRadius:8, fontSize:T.sm, fontWeight:600, cursor:'pointer', border:`1.5px solid ${form.fee_type===k?(k==='paid'?C.accent:'#059669'):C.border}`, background:form.fee_type===k?(k==='paid'?C.accentLt:'#DCFCE7'):'#fff', color:form.fee_type===k?(k==='paid'?C.accent:'#059669'):C.sub}}>{l}</button>
              ))}
            </div>
            {form.fee_type === 'paid' && (
              <div style={{display:'flex', gap:8, alignItems:'center'}}>
                <div style={{flex:1}}><div style={{fontSize:11, fontWeight:600, color:'#6B7280', marginBottom:4}}>금액</div><div style={{display:'flex', alignItems:'center', gap:4}}><input value={form.fee_amount} onChange={e => upd('fee_amount', e.target.value.replace(/\D/g,''))} style={{width:100, padding:'9px 12px', height:38, borderRadius:8, border:`1px solid ${C.border}`, fontSize:T.md, textAlign:'right', outline:'none', boxSizing:'border-box'}} /><span style={{fontSize:T.sm, color:C.muted}}>원</span></div></div>
                <div style={{flex:1}}><div style={{fontSize:11, fontWeight:600, color:'#6B7280', marginBottom:4}}>정산</div><div style={{display:'flex', gap:4}}>
                  {([['individual','개별입금'],['fa_settle','FA정산']] as const).map(([k,l]) => (<button key={k} onClick={() => upd('fee_method',k)} style={{flex:1, padding:0, height:38, borderRadius:8, fontSize:T.xs, fontWeight:form.fee_method===k?700:400, cursor:'pointer', border:`1px solid ${form.fee_method===k?C.accent:C.border}`, background:form.fee_method===k?C.accentLt:'#fff', color:form.fee_method===k?C.accent:C.sub}}>{l}</button>))}
                </div></div>
              </div>
            )}
            {form.fee_type === 'free' && <input value={form.fee_memo} onChange={e => upd('fee_memo',e.target.value)} placeholder="면제 사유" style={{width:'100%', padding:'9px 12px', height:38, borderRadius:8, border:`1px solid ${C.border}`, fontSize:T.md, outline:'none', boxSizing:'border-box'}} />}
          </div>
          {/* 상담 주제 */}
          <div style={{paddingTop:24, borderTop:'1px solid #F3F4F6'}}>
            <div style={{fontSize:13, fontWeight:700, color:'#1B3A5C', paddingBottom:8, borderBottom:'2px solid #E5E7EB', marginBottom:12}}>📝 상담 주제</div>
          <Field label="주제" value={form.topic} placeholder="상담 주제" onChange={v => upd('topic',v)} />
          <Field label="사전 메모" value={form.notes} component="textarea" placeholder="메모 (선택)" onChange={v => upd('notes',v)} />
          {/* 파일 첨부 */}
          <div style={{marginBottom:10}}>
            <label style={{fontSize:11,fontWeight:600,color:'#6B7280',display:'block',marginBottom:4}}>첨부 파일</label>
            <label style={{display:'inline-flex',alignItems:'center',gap:4,padding:'6px 14px',borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',cursor:'pointer',fontSize:T.sm,color:'#2E75B6',fontWeight:600}}>
              📎 파일 선택
              <input type="file" multiple onChange={e=>{const files=Array.from(e.target.files||[]);setForm(p=>({...p,ref_files:[...p.ref_files,...files]}));e.target.value='';}} style={{display:'none'}} />
            </label>
            {form.ref_files.length>0&&(
              <div style={{marginTop:6,display:'flex',flexDirection:'column',gap:3}}>
                {form.ref_files.map((f,i)=>(
                  <div key={i} style={{display:'flex',alignItems:'center',gap:6,padding:'4px 8px',borderRadius:4,background:'#F8FAFC',border:'1px solid #E5E7EB',fontSize:T.xs}}>
                    <span style={{flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{f.name}</span>
                    <span style={{color:'#9CA3AF',fontSize:T.xs}}>{(f.size/1024).toFixed(0)}KB</span>
                    <button onClick={()=>setForm(p=>({...p,ref_files:p.ref_files.filter((_,j)=>j!==i)}))} style={{background:'none',border:'none',color:'#DC2626',cursor:'pointer',fontSize:T.xs}}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          </div>
        </div>
        {toast && <div style={{position:'fixed', bottom:24, left:'50%', transform:'translateX(-50%)', padding:'12px 24px', borderRadius:10, background:C.text, color:'#fff', fontSize:14, fontWeight:600, zIndex:9999, boxShadow:'0 4px 12px rgba(0,0,0,0.15)'}}>{toast}</div>}
        {formConflicts.length > 0 && clashOk === clashSig && (
          <div role="status" style={{padding:'10px 20px', borderTop:`1px solid ${C.border}`, background:'#FFFBEB', fontSize:T.xs, color:'#92400E', lineHeight:1.6}}>
            같은 시각에 잡힌 일정이 있어요 — 「겹쳐도 등록」을 한 번 더 누르면 등록해요
            {formConflicts.map(x => <div key={x.date + x.time}>{x.date} {x.time} {x.r.overlaps.map(o => `${o.kind === 'lecture' ? '강의' : '상담'} ${o.time} ${o.label}`).join(', ')}</div>)}
          </div>
        )}
        <div style={{padding:'12px 20px', borderTop:`1px solid ${C.border}`, display:'flex', gap:8}}>
          <Btn variant="secondary" onClick={() => setMode('list')} style={{flex:1}}>취소</Btn>
          <button onClick={handleRegister} disabled={regBusy || (!form.fp_none && (!form.fp_name_input.trim() || !form.fp_phone.trim()))} style={{flex:2, padding:'10px 0', background:'linear-gradient(135deg, #111827, #1B3A5C)', color:'#fff', fontWeight:700, borderRadius:10, border:'none', fontSize:T.md, cursor:regBusy?'default':'pointer', opacity:(regBusy || (!form.fp_none && (!form.fp_name_input.trim() || !form.fp_phone.trim())))?0.5:1}}>{regBusy ? '등록하는 중…' : (formConflicts.length > 0 && clashOk === clashSig ? '겹쳐도 등록' : '등록')}</button>
        </div>
      </div>
    </div>
  );

  // ═══ 리스트 모드 (캘린더 + 하단 목록만) ═══
  return (
    <div style={{padding:20, overflowY:'auto', height:'100%'}}>
      {/* 삭제 확인 다이얼로그 */}
      {confirmDeleteId && (
        <div style={{position:'fixed', inset:0, zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center'}}>
          <div onClick={() => setConfirmDeleteId(null)} style={{position:'absolute', inset:0, background:'rgba(0,0,0,0.3)'}} />
          <div style={{position:'relative', padding:24, background:C.white, borderRadius:16, boxShadow:'0 8px 24px rgba(0,0,0,0.15)', width:360, textAlign:'center'}}>
            <div style={{fontSize:32, marginBottom:8}}>🗑️</div>
            <div style={{fontSize:T.md, fontWeight:700, marginBottom:4}}>상담 일정을 삭제하시겠습니까?</div>
            <div style={{fontSize:T.sm, color:C.muted, marginBottom:16}}>삭제된 일정은 복구할 수 없습니다</div>
            <div style={{display:'flex', gap:8}}>
              <Btn variant="secondary" onClick={() => setConfirmDeleteId(null)} style={{flex:1}}>취소</Btn>
              <button onClick={() => deleteConsult(confirmDeleteId)} style={{flex:1, padding:'10px 0', borderRadius:10, border:'none', background:'#DC2626', color:'#fff', fontSize:T.md, fontWeight:700, cursor:'pointer'}}>삭제</button>
            </div>
          </div>
        </div>
      )}

      {/* 헤더 */}
      <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:12, flexWrap:'wrap'}}>
        <button onClick={prevMonth} style={{padding:'6px 12px', borderRadius:8, border:`1px solid ${C.border}`, background:C.white, cursor:'pointer', fontSize:T.md}}>◀</button>
        <span style={{fontSize:T.xl, fontWeight:800, minWidth:110, textAlign:'center'}}>{year}년 {month+1}월</span>
        <button onClick={nextMonth} style={{padding:'6px 12px', borderRadius:8, border:`1px solid ${C.border}`, background:C.white, cursor:'pointer', fontSize:T.md}}>▶</button>
        <div style={{flex:1, minWidth:140, maxWidth:280}}><SearchInput value={search} onChange={setSearch} placeholder="업체·FP·주제" /></div>
        <span style={{display:'flex', gap:10, fontSize:T.xs, color:C.sub, alignItems:'center'}}><span><i style={{display:'inline-block', width:10, height:10, borderRadius:2, background:'#16A34A', marginRight:4, verticalAlign:-1}} />🤝 상담</span><span><i style={{display:'inline-block', width:10, height:10, borderRadius:2, background:'#7C3AED', marginRight:4, verticalAlign:-1}} />🎤 강의</span><span style={{color:'#DC2626', fontWeight:700}}>⚠ 시간 겹침</span></span>
        <Btn onClick={() => {setForm({...EMPTY_FORM, date: selDate}); setMode('register');}} style={{fontSize:T.md, padding:'8px 16px'}}>+ 약속 등록</Btn>
      </div>

      {/* 캘린더 */}
      <div style={{borderRadius:12, background:C.white, border:`1px solid ${C.border}`, overflow:'hidden', marginBottom:16}}>
        <div style={{display:'grid', gridTemplateColumns:'repeat(7, minmax(0, 1fr))'}}>
          {DAYS.map((d,i) => (
            <div key={d} style={{textAlign:'center', fontSize:T.sm, fontWeight:700, padding:'8px 4px', background:C.bgAlt, borderBottom:`1px solid ${C.border}`, color: i===0?'#DC2626':i===6?'#2563EB':C.sub}}>{d}</div>
          ))}
          {calDays.map((d,i) => {
            if (!d) return <div key={`e${i}`} style={{borderBottom:`1px solid ${C.border}`, borderRight:`1px solid ${C.border}`, minHeight:80}} />;
            const isToday = d.date === todayStr;
            const isSelected = d.date === selDate;
            const isSun = d.dow === 0; const isSat = d.dow === 6;
            const isHoliday = !!d.holiday;
            const dayColor = (isSun||isHoliday)?'#DC2626':isSat?'#2563EB':C.text;
            return (
              <div key={d.date} onClick={() => setSelDate(d.date)} style={{
                borderBottom:`1px solid ${C.border}`, borderRight:`1px solid ${C.border}`,
                minHeight:80, padding:'4px 5px', cursor:'pointer',
                background: isSelected?'#EDE9FE':isToday?'#F0FDF4':(isSun||isHoliday)?'#FEF2F2':isSat?'#EFF6FF':'transparent',
                outline: isSelected?`2px solid ${C.accent}`:'none', outlineOffset:'-2px',
              }}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:2}}>
                  <span style={{fontSize:T.sm, fontWeight:isToday?800:600, background:isToday?'#16A34A':'transparent', color:isToday?'#fff':dayColor, width:isToday?22:undefined, height:isToday?22:undefined, borderRadius:'50%', display:'inline-flex', alignItems:'center', justifyContent:'center'}}>{d.day}</span>
                  {(d.consults.length + (lecByDate[d.date]?.length || 0)) > 0 && <Badge bg={C.accent+'22'} color={C.accent} style={{fontSize:T.xs}}>{d.consults.length + (lecByDate[d.date]?.length || 0)}</Badge>}
                </div>
                {isHoliday && <div style={{fontSize:T.xs, color:'#DC2626', fontWeight:700, marginBottom:1, lineHeight:1.2}}>{d.holiday}</div>}
                {d.consults.slice(0,2).map((c,ci) => {
                  const isTent = c.confirm_status === 'tentative';
                  const slotForDay = isTent && c.candidate_slots?.length ? c.candidate_slots.find(s => d10(s.date) === d.date) : null;
                  const displayTime = (slotForDay?.time || c.time || '').slice(0,5);
                  const displayLoc = (slotForDay?.location || c.location || '');
                  const locParts = displayLoc.split(/\s+/);
                  const region = locParts.length >= 2 ? locParts.slice(0,2).join(' ') : locParts[0] || '';
                  return (
                    <div key={ci} onClick={(e) => { e.stopPropagation(); handleCardClick(c); }}
                      style={{fontSize:T.xs, lineHeight:1.3, padding:'2px 4px', borderRadius:3, marginBottom:1, cursor:'pointer',
                      background: isTent?'#FEF3C7':c.status==='completed'||c.status==='sent'?'#E5E7EB':'#DCFCE7',
                      color: isTent?'#92400E':c.status==='completed'||c.status==='sent'?'#6B7280':'#166534',
                      overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis',
                      boxShadow: clash.has(`consult:${c.id}`) ? 'inset 0 0 0 1px #DC2626' : 'none',
                    }}>{clash.has(`consult:${c.id}`) ? '⚠ ' : ''}{displayTime} {region} {c.fp_name||''} {c.company_name||'업체'}</div>
                  );
                })}
                {d.consults.length > 2 && <div style={{fontSize:8, color:C.muted, textAlign:'center'}}>+{d.consults.length-2}</div>}
                {(lecByDate[d.date] || []).slice(0, 2).map(l => (
                  <div key={'L'+l.id} onClick={(e) => { e.stopPropagation(); openLecture(l.id); }} title={`🎤 강의 · ${l.title}`}
                    style={{fontSize:T.xs, lineHeight:1.3, padding:'2px 4px', borderRadius:3, marginBottom:1, cursor:'pointer', background:'#EDE9FE', color:'#5B21B6', overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis',
                      boxShadow: clash.has(`lecture:${l.id}`) ? 'inset 0 0 0 1px #DC2626' : 'none'}}>
                    {clash.has(`lecture:${l.id}`) ? '⚠ ' : ''}🎤 {l.start_time} {l.title}
                  </div>
                ))}
                {(lecByDate[d.date]?.length || 0) > 2 && <div style={{fontSize:8, color:'#7C3AED', textAlign:'center'}}>강의 +{(lecByDate[d.date]?.length || 0)-2}</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* 선택 날짜 상세 */}
      <div style={{borderRadius:12, background:C.white, border:`1px solid ${C.border}`, padding:16}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10}}>
          <div>
            <span style={{fontSize:T.lg, fontWeight:700}}>{selDate}</span>
            <span style={{fontSize:T.md, color:C.muted, marginLeft:8}}>({DAYS[new Date(selDate+'T00:00:00').getDay()]})</span>
            {holidays[selDate] && <span style={{fontSize:T.sm, color:'#DC2626', fontWeight:600, marginLeft:8}}>{holidays[selDate]}</span>}
          </div>
          <span style={{fontSize:T.md, color:C.sub}}>{selDateConsults.length + (lecByDate[selDate]?.length || 0)}건</span>
        </div>
        {(lecByDate[selDate] || []).map(l => (
          <div key={'L'+l.id} onClick={() => openLecture(l.id)}
            style={{padding:'12px 14px', borderRadius:10, marginBottom:8, background:'#F5F3FF', border:`1px solid ${clash.has(`lecture:${l.id}`)?'#FCA5A5':'#DDD6FE'}`, cursor:'pointer'}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4}}>
              <span style={{fontSize:T.md, fontWeight:700}}>🎤 {l.title}</span>
              <Badge bg="#EDE9FE" color="#7C3AED">강의</Badge>
            </div>
            <div style={{fontSize:T.sm, color:C.muted}}>
              {[l.start_time, l.end_time].filter(Boolean).join(' ~ ') || '시간 미정'} · 📍{l.place || (l.mode === 'online' ? '온라인' : '장소 미정')}
              {(l.requester_org || l.requester_name) && <> · 🙋 {[l.requester_org, l.requester_name].filter(Boolean).join(' ')}</>}
              {clash.has(`lecture:${l.id}`) && <span style={{color:'#DC2626', fontWeight:700}}> · ⚠ 다른 일정과 시간이 겹칩니다</span>}
            </div>
          </div>
        ))}
        {selDateConsults.length === 0 ? (
          (lecByDate[selDate]?.length || 0) > 0 ? null : <div style={{textAlign:'center', padding:'24px 0', color:C.muted, fontSize:T.md}}>해당 날짜에 일정이 없습니다</div>
        ) : (
          selDateConsults.map(c => {
            const badge = getStatusBadge(c);
            return (
              <div key={c.id} onClick={() => handleCardClick(c)}
                style={{padding:'12px 14px', borderRadius:10, marginBottom:8, background:C.bg, border:`1px solid ${C.border}`, cursor:'pointer'}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4}}>
                  <span style={{fontSize:T.md, fontWeight:700}}>{c.company_name||'업체'}</span>
                  <Badge bg={badge.bg} color={badge.color}>{badge.label}</Badge>
                </div>
                <div style={{fontSize:T.sm, color:C.muted, marginBottom:6}}>
                  {c.fp_name||'FP 미지정'} · {c.time||'시간 미정'} · 📍{c.location||'장소 미정'}
                </div>
                <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
                  <button onClick={(e) => {e.stopPropagation(); setConfirmDeleteId(c.id);}}
                    style={{fontSize:T.xs, padding:'4px 10px', background:'none', border:`1px solid #DC262633`, borderRadius:6, color:'#DC2626', cursor:'pointer'}}>🗑️ 삭제</button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Post-registration confirmation banner */}
      {postRegConsult && !postRegSmsPreview && (
        <div style={{position:'fixed', inset:0, zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center'}}>
          <div onClick={() => { setPostRegConsult(null); showToast('상담이 등록되었습니다'); }} style={{position:'absolute', inset:0, background:'rgba(0,0,0,0.3)'}} />
          <div style={{position:'relative', padding:24, background:C.white, borderRadius:16, boxShadow:'0 8px 24px rgba(0,0,0,0.15)', width:400, textAlign:'center'}}>
            <div style={{fontSize:32, marginBottom:8}}>&#x2705;</div>
            <div style={{fontSize:T.lg, fontWeight:700, marginBottom:4}}>상담이 등록되었습니다</div>
            <div style={{fontSize:T.md, color:C.muted, marginBottom:16}}>FP에게 SMS로 알리시겠습니까?</div>
            <div style={{display:'flex', gap:8}}>
              <Btn variant="secondary" onClick={() => { setPostRegConsult(null); showToast('상담이 등록되었습니다'); }} style={{flex:1}}>닫기</Btn>
              <Btn onClick={() => { setPostRegSmsText(buildConsultSmsText(postRegConsult)); setPostRegSmsPreview(true); }} style={{flex:1}}>FP에게 발송</Btn>
            </div>
          </div>
        </div>
      )}

      {/* Post-registration SMS preview modal */}
      {postRegConsult && postRegSmsPreview && (
        <div style={{position:'fixed', inset:0, zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center'}}>
          <div onClick={() => setPostRegSmsPreview(false)} style={{position:'absolute', inset:0, background:'rgba(0,0,0,0.3)'}} />
          <div style={{position:'relative', width:420, background:C.white, borderRadius:16, boxShadow:'0 8px 24px rgba(0,0,0,0.15)', overflow:'hidden'}}>
            <div style={{padding:'14px 20px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between'}}>
              <span style={{fontSize:T.lg, fontWeight:700}}>SMS 발송 미리보기</span>
              <button onClick={() => setPostRegSmsPreview(false)} style={{width:28, height:28, borderRadius:6, border:`1px solid ${C.border}`, background:C.white, cursor:'pointer', fontSize:T.md}}>&#x2715;</button>
            </div>
            <div style={{padding:'16px 20px'}}>
              <div style={{fontSize:T.sm, color:C.muted, marginBottom:4}}>수신: {postRegConsult.fp_name || 'FP'} ({fps.find(f => f.id === postRegConsult.fp_id)?.phone || postRegConsult.fp_phone || ''})</div>
              <textarea value={postRegSmsText} onChange={e => setPostRegSmsText(e.target.value)} rows={7}
                style={{width:'100%', padding:'12px', borderRadius:10, border:`1px solid ${C.border}`, fontSize:T.md, resize:'vertical', outline:'none', boxSizing:'border-box', lineHeight:1.6}} />
              <div style={{fontSize:T.xs, color:C.muted, textAlign:'right', marginTop:4}}>{postRegSmsText.length}자</div>
            </div>
            <div style={{padding:'12px 20px', borderTop:`1px solid ${C.border}`, display:'flex', gap:8}}>
              <Btn variant="secondary" onClick={() => setPostRegSmsPreview(false)} style={{flex:1}}>취소</Btn>
              <Btn onClick={handlePostRegSms} disabled={postRegSmsSending} style={{flex:1}}>{postRegSmsSending ? '발송 중...' : 'SMS 발송'}</Btn>
            </div>
          </div>
        </div>
      )}

      {toast && <div style={{position:'fixed', bottom:24, left:'50%', transform:'translateX(-50%)', padding:'12px 24px', borderRadius:10, background:C.text, color:'#fff', fontSize:T.md, fontWeight:600, zIndex:9999}}>{toast}</div>}
    </div>
  );
}

/** 등록 폼 — 시간이 겹치는 일정 안내 (저장은 막지 않고, 등록 시 한 번 더 확인) */
function ConflictNote({ items }: { items: { date: string; time: string; r: ConflictResult }[] }) {
  if (!items.length) return null;
  return (
    <div style={{margin:'-2px 0 10px', padding:'10px 12px', borderRadius:10, background:'#FEF2F2', border:'1px solid #FCA5A5', fontSize:T.sm, color:'#991B1B', lineHeight:1.6}}>
      <div style={{fontWeight:800, marginBottom:2}}>⚠ 시간이 겹치는 일정이 있습니다</div>
      {items.map(x => x.r.overlaps.map(b => (
        <div key={x.date + x.time + b.kind + b.id}>{x.date.slice(5).replace('-', '.')} · {b.kind === 'lecture' ? '🎤 강의' : '🤝 상담'} {b.time} {b.label}</div>
      )))}
    </div>
  );
}
